import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import {
  FC_SYSTEM_AUTH_ACCESS_TOKEN,
  FC_SYSTEM_AUTH_REFRESH_TOKEN,
  refreshAuthToken,
} from 'requests/auth';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { EAlertVariants } from 'components/atoms/Alert';
import { ApiEndpoints } from 'models/apiEndpoints';
import { FC_SYSTEM_APP_LANG } from 'requests/language';
import { ILoginBanInfo } from 'models/auth';
import { useTranslations } from 'hooks/useTranslations';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorHandling?: boolean;
  }
}

const REST_API_URL = process.env.REACT_APP_API_URL || 'http://35.157.237.44:8082/api/';
const apiClient: AxiosInstance = axios.create({
  baseURL: REST_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: process.env.REACT_APP_REQUEST_TIMEOUT_MS
    ? parseInt(process.env.REACT_APP_REQUEST_TIMEOUT_MS, 10)
    : 80000,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const AxiosInterceptor = () => {
  const navigate = useNavigate();
  const { setAlert, clearAlert } = useAlert();
  const { t } = useTranslations();
  const { pathname }: any = useLocation();
  const { id = '' } = useParams<{ id: string }>();

  const latest = useRef({ t, pathname, id, setAlert, clearAlert });
  latest.current = { t, pathname, id, setAlert, clearAlert };

  useEffect(() => {
    const t = (key: string) => latest.current.t(key);
    const setAlert: typeof latest.current.setAlert = (...args) =>
      latest.current.setAlert(...args);
    const clearAlert: typeof latest.current.clearAlert = (...args) =>
      latest.current.clearAlert(...args);

    const reqInterceptor = async (
      req: InternalAxiosRequestConfig,
    ): Promise<InternalAxiosRequestConfig> => {
      const token = localStorage.getItem(FC_SYSTEM_AUTH_ACCESS_TOKEN);
      const selectedLanguage = localStorage.getItem(FC_SYSTEM_APP_LANG);

      if (req?.headers && req.url !== ApiEndpoints.GET_LICENCE_BASIC_INFO()) {
        if (selectedLanguage) {
          req.headers.set('Accept-Language', selectedLanguage);
        }
        if (token && req.url?.indexOf(ApiEndpoints.REFRESH_AUTH_TOKEN()) === -1) {
          req.headers.set('Authorization', `Bearer ${token}`);
        }
      }

      if (req.url === ApiEndpoints.LOG_OUT()) {
        localStorage.removeItem(FC_SYSTEM_AUTH_ACCESS_TOKEN);
        localStorage.removeItem(FC_SYSTEM_AUTH_REFRESH_TOKEN);
        navigate(ERouteLinks.Auth);
      }

      return req;
    };

    const resInterceptor = async (response: AxiosResponse) => {
      if (response.config.url?.includes(ApiEndpoints.LOG_OUT()) && response.status === 200) {
        setAlert(ActionTypes.SET_AUTH_ALERT, {
          text: t('authFailure.successfullyLoggedOut.text'),
          textKey: 'authFailure.successfullyLoggedOut.text',
          variant: EAlertVariants.success,
        });
      }
      return response;
    };

    const errInterceptor = async (error: AxiosError<any>) => {
      const isLoginError = error.request?.responseURL.indexOf(ApiEndpoints.LOG_IN()) !== -1;
      const isVerifyOfflineError =
        error.request?.responseURL.indexOf(ApiEndpoints.VERIFY_OFFLINE_AUTH_CODE()) !== -1;
      const isRefreshAuthTokenError =
        error.request?.responseURL.indexOf(ApiEndpoints.REFRESH_AUTH_TOKEN()) !== -1;
      const isChangePasswordError =
        error.request?.responseURL.indexOf(ApiEndpoints.ACCOUNT_CHANGE_PASSWORD()) !== -1;
      const isLicenceError =
        error.request?.responseURL.indexOf(ApiEndpoints.GET_LICENCE_BASIC_INFO()) !== -1;
      const isCaseDecisionError =
        error.request?.responseURL.indexOf('weapon-licence-cases') !== -1 &&
        error.request?.responseURL.endsWith('/decision');
      const isAccessError = error.response?.data?.exception === 'AccessDeniedException';
      const isRefreshableTokenError = error.response?.status === 403 && !isAccessError;

      if (
        axios.isCancel(error) ||
        (error.config?.skipGlobalErrorHandling && !isRefreshableTokenError)
      ) {
        return Promise.reject(error);
      }

      let isAuditLogError = false;

      if (
        [
          'FirearmNotFoundException',
          'LegalNotFoundException',
          'ApiKeyNotFoundException',
          'CustomFieldNotFoundException',
          'RoleNotFoundException',
        ].includes(error?.response?.data?.exception) &&
        latest.current.pathname.indexOf(
          ERouteLinks.AuditLogViewPage.replace(':id', latest.current.id),
        ) !== -1
      ) {
        isAuditLogError = true;
      }

      const authFailure = (text: string, textKey?: string) => {
        setAlert(ActionTypes.SET_AUTH_ALERT, {
          text,
          textKey,
          variant: EAlertVariants.error,
        });
        localStorage.removeItem(FC_SYSTEM_AUTH_ACCESS_TOKEN);
        localStorage.removeItem(FC_SYSTEM_AUTH_REFRESH_TOKEN);
        navigate(ERouteLinks.Auth);
        if (error && process.env.REACT_APP_DEV_LOGS === 'true') {
          // eslint-disable-next-line no-console
          console.log('error', error);
        }
      };

      const authFailureWithKey = (textKey: string) => authFailure(t(textKey), textKey);

      if (error.response?.status === 401 && isLoginError) {
        authFailure(error.response?.data.message);
      } else if (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ERR_CONNECTION_ABORTED' ||
        error.code === 'ERR_CONNECTION_TIMED_OUT'
      ) {
        authFailure(error?.message);
      } else if (error.code === 'ECONNABORTED') {
        authFailureWithKey('authFailure.sessionEndedNetworkConnectionInterrupted.text');
      } else if (error.response?.status === 401 && isVerifyOfflineError) {
        localStorage.removeItem(FC_SYSTEM_AUTH_ACCESS_TOKEN);
        localStorage.removeItem(FC_SYSTEM_AUTH_REFRESH_TOKEN);
        if (error && process.env.REACT_APP_DEV_LOGS === 'true') {
          // eslint-disable-next-line no-console
          console.log('error', error);
        }
      } else if (
        error.response?.status === 401 &&
        error.response?.data?.exception === 'InsufficientAuthenticationException'
      ) {
        authFailureWithKey('authFailure.sessionExpiredLoggedOut.text');
      } else if (error.response?.status === 401) {
        authFailureWithKey('authFailure.sessionExpired.text');
      } else if (
        error.response?.status === 403 &&
        !isLoginError &&
        !isRefreshAuthTokenError &&
        !isChangePasswordError
      ) {
        if (isAccessError) {
          navigate(ERouteLinks.AccessDeniedPage);
        } else {
          const originalRequest = error.config;
          if (!originalRequest) {
            return Promise.reject(error);
          }

          if (!isRefreshing) {
            isRefreshing = true;
            const refreshToken = localStorage.getItem(FC_SYSTEM_AUTH_REFRESH_TOKEN);

            if (!refreshToken) {
              isRefreshing = false;
              authFailureWithKey('authFailure.sessionExpired.text');
              return Promise.reject(error);
            }

            const selectedLanguage = localStorage.getItem(FC_SYSTEM_APP_LANG);
            try {
              const res = await refreshAuthToken({
                refreshToken,
              });

              if (res?.accessToken && res?.refreshToken) {
                originalRequest.headers.set('Authorization', `Bearer ${res.accessToken}`);
                if (selectedLanguage) {
                  originalRequest.headers.set('Accept-Language', selectedLanguage);
                }
                localStorage.setItem(FC_SYSTEM_AUTH_ACCESS_TOKEN, res.accessToken);
                localStorage.setItem(FC_SYSTEM_AUTH_REFRESH_TOKEN, res.refreshToken);

                processQueue(null, res.accessToken);
              }

              isRefreshing = false;
              // eslint-disable-next-line consistent-return
              return await apiClient(originalRequest);
            } catch (e: any) {
              processQueue(e, null);
              isRefreshing = false;
              authFailureWithKey('authFailure.sessionExpired.text');
              return Promise.reject(e);
            }
          } else {
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token?: unknown) => {
                  if (typeof token === 'string') {
                    originalRequest.headers.set('Authorization', `Bearer ${token}`);
                  }
                  resolve(apiClient(originalRequest));
                },
                reject: (err: any) => {
                  reject(err);
                },
              });
            });
          }
        }
      } else if (error.response?.status === 403) {
        if (isChangePasswordError) {
          // eslint-disable-next-line consistent-return
          return Promise.reject(error);
        }

        if (isLoginError) {
          const loginBanInfo = error.response.data as ILoginBanInfo;
          if (loginBanInfo.minutes && loginBanInfo.attempts) {
            authFailureWithKey('authFailure.accountBlocked.text');
          }
        } else {
          const accessToken = localStorage.getItem(FC_SYSTEM_AUTH_ACCESS_TOKEN);
          const refreshToken = localStorage.getItem(FC_SYSTEM_AUTH_REFRESH_TOKEN);

          if (!!accessToken && !!refreshToken) {
            navigate(ERouteLinks.AccessDeniedPage);
          } else {
            authFailureWithKey('authFailure.sessionExpired.text');
          }
        }
        // eslint-disable-next-line no-dupe-else-if
      } else if (
        error.response?.status === 404 &&
        !isLicenceError &&
        !isAuditLogError &&
        !isCaseDecisionError
      ) {
        navigate(ERouteLinks.NotFoundPage);
      } else if (error.response?.status === 404 && isLicenceError) {
        navigate(ERouteLinks.StartLicencePage);
      } else if (error.response?.status === 404 && isAuditLogError) {
        setAlert(
          ActionTypes.SET_AUDIT_LOG_SAME_PAGE_ALERT,
          {
            text: t('auditLogDetails.error.text'),
            variant: EAlertVariants.error,
          },
          true,
        );
      } else if (error.response?.status === 500) {
        navigate(ERouteLinks.ServerErrorPage);
      } else {
        clearAlert(ActionTypes.CLEAR_AUTH_ALERT);
      }
      // eslint-disable-next-line consistent-return
      return Promise.reject(error);
    };

    const interceptorReq = apiClient.interceptors.request.use(reqInterceptor, errInterceptor);
    const interceptorResp = apiClient.interceptors.response.use(
      resInterceptor,
      errInterceptor,
    );

    return () => {
      apiClient.interceptors.response.eject(interceptorResp);
      apiClient.interceptors.request.eject(interceptorReq);
    };
  }, [navigate]);

  return null;
};

export { apiClient, AxiosInterceptor };
